import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IAccountMovement, IDocumentFiscalTypes } from "src/models/Accounting/Accounting.models";
import { IAccountingEntriesReport, IAccountingInvoicesReport, IAccountingStatusResultReport, ISalesByCustomerGroupedReport, ISalesByCustomerGroupedReportResponse } from "src/models/Accounting/AccountingReports.models";
import { IProductSalesReport } from "src/models/Inventory/InventoryProduct.models";

interface AccountMovementState {
    documentFiscalTypes: IDocumentFiscalTypes[];
    accountMovements: IAccountMovement[];
    accountMovementToEdit: IAccountMovement | null;
    invoiceReport: IAccountingInvoicesReport | null;
    statusResultReport: IAccountingStatusResultReport | null;
    accountingEntriesReport: IAccountingEntriesReport[];
    salesByProduct: IProductSalesReport[];
    salesByCustomerGroupedReport: ISalesByCustomerGroupedReportResponse | null;
}

const initialState: AccountMovementState = {
    documentFiscalTypes: [],
    accountMovements: [],
    accountMovementToEdit: null,
    invoiceReport: null,
    statusResultReport: null,
    accountingEntriesReport: [],
    salesByProduct: [],
    salesByCustomerGroupedReport: null,
};

const AccountMovementSlice = createSlice({
    name: "AccountMovement",
    initialState,
    reducers: {
        setDocumentFiscalTypesList: (
            state,
            action: PayloadAction<IDocumentFiscalTypes[]>
        ) => {
            state.documentFiscalTypes = action.payload;
        },
        concatListDocumentFiscalTypes: (
            state: AccountMovementState,
            action: PayloadAction<IDocumentFiscalTypes[]>
        ) => {
            state.documentFiscalTypes = [
                ...state.documentFiscalTypes.concat(action.payload),
            ];
        },
        setAccountMovementList: (
            state,
            action: PayloadAction<IAccountMovement[]>
        ) => {
            state.accountMovements = action.payload;
        },
        concatListAccountMovement: (
            state: AccountMovementState,
            action: PayloadAction<IAccountMovement[]>
        ) => {
            state.accountMovements = [
                ...state.accountMovements.concat(action.payload),
            ];
        },
        setAccountMovementToEdit: (
            state,
            action: PayloadAction<IAccountMovement | null>
        ) => {
            state.accountMovementToEdit = action.payload;
        },
        addAccountMovement: (state, action: PayloadAction<IAccountMovement>) => {
            state.accountMovements.push(action.payload);
        },
        updateAccountMovement: (state, action: PayloadAction<IAccountMovement>) => {
            let itemIndex = state.accountMovements.findIndex(
                item => item.accountMovementId === action.payload.accountMovementId
            );
            if (itemIndex !== -1) state.accountMovements[itemIndex] = action.payload;
        },
        deleteAccountMovement: (state, action: PayloadAction<number>) => {
            const newArray = state.accountMovements.filter(
                item => item.accountMovementId !== action.payload
            );
            state.accountMovements = newArray;
        },

        // Accounting Reports
        setInvoiceReport: (state, action: PayloadAction<IAccountingInvoicesReport | null>
        ) => {
            state.invoiceReport = action.payload;
        },
        setStatusResultReport: (state, action: PayloadAction<IAccountingStatusResultReport | null>
        ) => {
            state.statusResultReport = action.payload;
        },
        setAccountingEntriesReport: (state, action: PayloadAction<IAccountingEntriesReport[]>
        ) => {
            state.accountingEntriesReport = action.payload;
        },
        setSalesByProductReport: (state, action: PayloadAction<IProductSalesReport[]>
        ) => {
            state.salesByProduct = action.payload;
        },
        concatSalesByProductReport: (
            state: AccountMovementState,
            action: PayloadAction<IProductSalesReport[]>
        ) => {
            state.salesByProduct = [
                ...state.salesByProduct.concat(action.payload),
            ];
        },
        setSalesByCustomerGroupedReport: (
            state,
            action: PayloadAction<ISalesByCustomerGroupedReportResponse | null>
        ) => {
            state.salesByCustomerGroupedReport = action.payload;
        },
        concatSalesByCustomerGroupedReportDetails: (
            state: AccountMovementState,
            action: PayloadAction<ISalesByCustomerGroupedReport[]>
        ) => {
            if (state.salesByCustomerGroupedReport) {
                state.salesByCustomerGroupedReport.details = [
                    ...state.salesByCustomerGroupedReport.details.concat(action.payload),
                ];
            }
        },
    },
});

export const {
    setDocumentFiscalTypesList,
    concatListDocumentFiscalTypes,

    setAccountMovementList,
    concatListAccountMovement,
    setAccountMovementToEdit,
    addAccountMovement,
    updateAccountMovement,
    deleteAccountMovement,
    
    // Accounting Reports
    setInvoiceReport,
    setStatusResultReport,
    setAccountingEntriesReport,
    setSalesByProductReport,
    concatSalesByProductReport,
    setSalesByCustomerGroupedReport,
    concatSalesByCustomerGroupedReportDetails,
} = AccountMovementSlice.actions;

export default AccountMovementSlice.reducer;